import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import {
  FEATURE_DESCRIPTIONS,
  FeatureMap,
  featureLabel,
  getFeatureKeys,
  getOrganizationStats,
  OrganizationStats,
  setOrganizationPublicBrowse,
  updateOrganizationFeatures,
} from "../../services/superadminService";
import { getErrorMessage } from "../../utils/apiResponse";

const WINDOWS = [7, 30, 90];

/**
 * Reserved status colors — never reused as generic series colors. Validated for
 * CVD separation and 3:1 contrast against a light surface. Unknown statuses fall
 * back to the neutral, and every segment carries a written label, so identity is
 * never colour-alone.
 */
const STATUS_COLORS: Record<string, string> = {
  DONE: "#1D8A57",
  COMPLETED: "#1D8A57",
  CONFIRMED: "#1D8A57",
  PENDING: "#BC8A00",
  CANCELLED: "#C4362A",
  CANCELED: "#C4362A",
};
const NEUTRAL_STATUS = "#6B7280";

const statusColor = (status: string) =>
  STATUS_COLORS[status.toUpperCase()] || NEUTRAL_STATUS;

const prettyStatus = (status: string) =>
  status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ");

const StatTile: React.FC<{
  label: string;
  value: React.ReactNode;
  caption?: string;
}> = ({ label, value, caption }) => (
  <Paper
    elevation={0}
    sx={{
      border: `1px solid ${COLORS.BorderColor}`,
      borderRadius: 2,
      p: 2,
      flex: "1 1 150px",
      minWidth: 150,
    }}
  >
    <Typography
      sx={{
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: COLORS.placeHolderColor,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: "1.7rem", fontWeight: 700, lineHeight: 1.25 }}>
      {value}
    </Typography>
    {caption && (
      <Typography sx={{ fontSize: "0.76rem", color: COLORS.placeHolderColor }}>
        {caption}
      </Typography>
    )}
  </Paper>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 1.25 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/** Proportional breakdown, one segment per status actually present. */
const StatusBreakdown: React.FC<{ byStatus: Record<string, number> }> = ({
  byStatus,
}) => {
  const entries = Object.entries(byStatus || {}).filter(([, count]) => count > 0);
  const total = entries.reduce((sum, [, count]) => sum + count, 0);

  if (!total) {
    return (
      <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.9rem" }}>
        No bookings in this window.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", gap: "2px", height: 14, mb: 1.5, width: "100%" }}>
        {entries.map(([status, count], index) => (
          <Tooltip
            key={status}
            title={`${prettyStatus(status)}: ${count} (${Math.round(
              (count / total) * 100
            )}%)`}
          >
            <Box
              sx={{
                width: `${(count / total) * 100}%`,
                bgcolor: statusColor(status),
                borderTopLeftRadius: index === 0 ? 4 : 0,
                borderBottomLeftRadius: index === 0 ? 4 : 0,
                borderTopRightRadius: index === entries.length - 1 ? 4 : 0,
                borderBottomRightRadius: index === entries.length - 1 ? 4 : 0,
              }}
            />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
        {entries.map(([status, count]) => (
          <Box
            key={status}
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "2px",
                bgcolor: statusColor(status),
              }}
            />
            <Typography sx={{ fontSize: "0.84rem" }}>
              {prettyStatus(status)}
            </Typography>
            <Typography
              sx={{ fontSize: "0.84rem", color: COLORS.placeHolderColor }}
            >
              {count} · {Math.round((count / total) * 100)}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const OrganizationDetails: React.FC = () => {
  const { organizationId = "" } = useParams();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [features, setFeatures] = useState<FeatureMap>({});
  const [featureKeys, setFeatureKeys] = useState<string[]>([]);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [publicBrowse, setPublicBrowse] = useState(false);
  const [savingPublicBrowse, setSavingPublicBrowse] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFeatureKeys()
      .then(setFeatureKeys)
      .catch(() => setFeatureKeys([]));
  }, []);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const result = await getOrganizationStats(organizationId, days);
      setStats(result);
      setFeatures(result.features || {});
      setPublicBrowse(!!result.organization?.publicBrowseEnabled);
    } catch {
      // The interceptor already surfaced the message.
    }
    setLoading(false);
  }, [organizationId, days]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (key: string, enabled: boolean) => {
    const previous = features;
    setFeatures((prev) => ({ ...prev, [key]: enabled })); // optimistic
    setSavingKey(key);
    try {
      const result = await updateOrganizationFeatures(organizationId, [
        { feature: key, enabled },
      ]);
      // Reconcile from the full map the API returns, not from the guess.
      setFeatures(result.features);
      toast.success(
        `${featureLabel(key)} ${enabled ? "enabled" : "hidden"} for ${
          stats?.organization?.name ?? "this organization"
        }.`,
        { position: "top-right" }
      );
    } catch (err: any) {
      setFeatures(previous); // roll back
      toast.error(getErrorMessage(err, "Could not update the feature."), {
        position: "top-right",
      });
    }
    setSavingKey(null);
  };

  const handlePublicBrowseToggle = async (enabled: boolean) => {
    const previous = publicBrowse;
    setPublicBrowse(enabled); // optimistic
    setSavingPublicBrowse(true);
    try {
      const result = await setOrganizationPublicBrowse(organizationId, enabled);
      setPublicBrowse(result.publicBrowseEnabled);
      toast.success(
        `Public catalogue ${enabled ? "opened" : "closed"} for ${
          stats?.organization?.name ?? "this organization"
        }.`,
        { position: "top-right" }
      );
    } catch (err: any) {
      setPublicBrowse(previous); // roll back
      toast.error(
        getErrorMessage(err, "Could not update public browsing."),
        { position: "top-right" }
      );
    }
    setSavingPublicBrowse(false);
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }}>Organization not found.</Typography>
      </Box>
    );
  }

  const org = stats.organization;
  const byStatus = stats.bookings?.byStatusInWindow || {};
  const statusTotal = Object.values(byStatus).reduce((a, b) => a + b, 0);
  const cancelled = (byStatus.CANCELLED ?? 0) + (byStatus.CANCELED ?? 0);
  const cancelRate = statusTotal ? Math.round((cancelled / statusTotal) * 100) : 0;
  const cancelIsHigh = cancelRate >= 20;
  const orderedKeys = featureKeys.length
    ? featureKeys
    : Object.keys(features || {});

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/superadmin/organizations")}
        sx={{ textTransform: "none", mb: 1, color: COLORS.placeHolderColor }}
      >
        All organizations
      </Button>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.6rem" }}>
              {org.name}
            </Typography>
            {org.isDeleted && (
              <Chip
                size="small"
                label="Deleted"
                sx={{ bgcolor: "#f7e5e3", color: "#a32b22", fontWeight: 700 }}
              />
            )}
          </Box>
          <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.88rem" }}>
            +{org.country} · {org.timezone} · joined{" "}
            {format(new Date(org.createdAt), "d MMM yyyy")}
          </Typography>
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={days}
          onChange={(_, value) => value && setDays(value)}
        >
          {WINDOWS.map((option) => (
            <ToggleButton
              key={option}
              value={option}
              sx={{ textTransform: "none", px: 2 }}
            >
              {option}d
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Section title={`Bookings · last ${stats.windowDays} days`}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
          <StatTile
            label="In window"
            value={stats.bookings.inWindow}
            caption={`${stats.bookings.total} all time`}
          />
          <StatTile label="Upcoming" value={stats.bookings.upcoming} />
          <StatTile
            label="Last booked"
            value={
              stats.bookings.lastCreatedAt
                ? formatDistanceToNow(new Date(stats.bookings.lastCreatedAt), {
                    addSuffix: true,
                  })
                : "Never"
            }
          />
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${cancelIsHigh ? "#C4362A" : COLORS.BorderColor}`,
              borderRadius: 2,
              p: 2,
              flex: "1 1 190px",
              minWidth: 190,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: COLORS.placeHolderColor,
              }}
            >
              Cancellation rate
            </Typography>
            <Typography
              sx={{ fontSize: "1.7rem", fontWeight: 700, lineHeight: 1.25 }}
            >
              {cancelRate}%
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {cancelIsHigh ? (
                <WarningAmberIcon sx={{ fontSize: 15, color: "#C4362A" }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: 15, color: "#1D8A57" }} />
              )}
              <Typography
                sx={{ fontSize: "0.76rem", color: COLORS.placeHolderColor }}
              >
                {cancelIsHigh ? "High — worth a call" : "Healthy"} · {cancelled} of{" "}
                {statusTotal}
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Paper
          elevation={0}
          sx={{ border: `1px solid ${COLORS.BorderColor}`, borderRadius: 2, p: 2 }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: COLORS.placeHolderColor,
              mb: 1.25,
            }}
          >
            Status breakdown
          </Typography>
          <StatusBreakdown byStatus={byStatus} />
        </Paper>
      </Section>

      <Section title="People">
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <StatTile label="Clients" value={stats.people.clients} />
          <StatTile label="Staff" value={stats.people.staff} />
          <StatTile label="Coaches" value={stats.people.coaches} />
        </Box>
      </Section>

      <Section title="Setup">
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <StatTile label="Businesses" value={stats.setup.businesses} />
          <StatTile label="Units" value={stats.setup.units} />
          <StatTile label="Resources" value={stats.setup.resources} />
          <StatTile label="Products" value={stats.setup.products} />
        </Box>
      </Section>

      <Section title="Modules">
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <StatTile
            label="Classes"
            value={stats.classes.offerings}
            caption={`${stats.classes.enrollments} enrollments`}
          />
          <StatTile
            label="Packages"
            value={stats.packages.offered}
            caption={`${stats.packages.sold} sold`}
          />
          <StatTile
            label="Stock moves"
            value={stats.stock.movementsInWindow}
            caption={`in the last ${stats.windowDays} days`}
          />
          <StatTile
            label="Notifications"
            value={stats.notifications.sentThisMonth}
            caption="sent this month"
          />
        </Box>
      </Section>

      <Section title="Features">
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${COLORS.BorderColor}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {orderedKeys.map((key, index) => (
            <Box key={key}>
              {index > 0 && <Divider />}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    {featureLabel(key)}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.8rem", color: COLORS.placeHolderColor }}
                  >
                    {FEATURE_DESCRIPTIONS[key] ||
                      "Controls the matching sections of the backoffice."}
                  </Typography>
                </Box>
                <Switch
                  checked={!!features[key]}
                  disabled={savingKey === key}
                  onChange={(e) => handleToggle(key, e.target.checked)}
                />
              </Box>
            </Box>
          ))}
        </Paper>
        <Typography
          sx={{ fontSize: "0.8rem", color: COLORS.placeHolderColor, mt: 1 }}
        >
          Turning a feature off hides it. Loyalty points, classes and stock records
          all survive, and switching it back on restores access exactly as it was.
        </Typography>
      </Section>

      <Section title="Public catalogue">
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${COLORS.BorderColor}`,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 2,
            py: 1.5,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
              Public browsing
            </Typography>
            <Typography
              sx={{ fontSize: "0.8rem", color: COLORS.placeHolderColor }}
            >
              Lets visitors browse this organization's catalogue and schedule
              without signing in.
            </Typography>
          </Box>
          <Switch
            checked={publicBrowse}
            disabled={savingPublicBrowse}
            onChange={(e) => handlePublicBrowseToggle(e.target.checked)}
          />
        </Paper>
        <Typography
          sx={{ fontSize: "0.8rem", color: COLORS.placeHolderColor, mt: 1 }}
        >
          Takes effect on the server immediately, but visitors who already loaded
          the catalogue may keep seeing it for up to 5 minutes after it's turned
          off.
        </Typography>
      </Section>
    </Box>
  );
};

export default OrganizationDetails;
